// src/components/SurgicalBookingModal.jsx
// Modal for sending a surgical booking request to the surgery coordinator.
// Uses createPortal to avoid z-index issues.

import { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { db } from "../lib/db";
import { logAudit } from "../utils/auditLog";
import { apiFetch, getAuthToken } from "../lib/api";

const URGENCY_OPTIONS = [
  "Elective",
  "Semi-urgent (2–4 weeks)",
  "Urgent (< 1 week)",
  "Emergent",
];

const ANESTHESIA_OPTIONS = [
  "General",
  "Spinal",
  "Regional block",
  "MAC (monitored anesthesia care)",
  "Local only",
];

const LABEL_ST = {
  fontSize: 11, fontWeight: 600, color: "var(--text-3)",
  textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4,
};
const INPUT_ST = {
  width: "100%", padding: "7px 10px", borderRadius: 6,
  border: "1px solid var(--border-2)", background: "var(--navy-3)",
  color: "var(--text-1)", fontSize: 13, outline: "none", boxSizing: "border-box",
};
const SELECT_ST = { ...INPUT_ST, cursor: "pointer" };

export default function SurgicalBookingModal({ order, patient, onClose, onSent }) {
  const [form, setForm] = useState({
    procedure:         order?.details?.procedure  || order?.description || "",
    urgency:           order?.details?.urgency    || "Elective",
    anesthesia:        order?.details?.anesthesia || "General",
    implants:          order?.details?.implants   || "",
    preop_clearance:   order?.details?.preop_clearance ?? false,
    facility:          order?.details?.facility   || "",
    target_date:       order?.details?.target_date || "",
    notes:             order?.notes               || "",
    coordinator_email: order?.details?.coordinator_email || "",
  });
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [error,    setError]    = useState("");
  const [preview,  setPreview]  = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const patientName = patient ? `${patient.first_name} ${patient.last_name}` : "Unknown patient";
  const patientMrn  = patient?.mrn || "—";

  const emailPreview = [
    `SURGICAL BOOKING REQUEST — Epiphany MD`,
    ``,
    `Patient:   ${patientName}  ·  MRN: ${patientMrn}`,
    `Procedure: ${form.procedure || "(not specified)"}`,
    `Urgency:   ${form.urgency}`,
    `Target date: ${form.target_date || "TBD"}`,
    ``,
    `Anesthesia:  ${form.anesthesia}`,
    `Implants / equipment:  ${form.implants || "None specified"}`,
    `Pre-op clearance required:  ${form.preop_clearance ? "Yes" : "No"}`,
    `Preferred facility:  ${form.facility || "Not specified"}`,
    ``,
    `Ordered by:  ${order?.ordered_by || "—"}`,
    `Order date:  ${order?.order_date  || "—"}`,
    ``,
    `Notes to coordinator:`,
    form.notes || "(none)",
    ``,
    `Please log in to confirm OR date/time and enter details in Epiphany MD.`,
  ].join("\n");

  const handleSend = useCallback(async () => {
    if (!form.procedure.trim())         { setError("Procedure name is required."); return; }
    if (!form.coordinator_email.trim()) { setError("Coordinator email is required."); return; }
    setError("");
    setSending(true);

    try {
      const token = await getAuthToken();

      const res = await apiFetch("notify-surgery-coordinator", {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderId:          order.id,
          patientName,
          patientMrn,
          procedure:        form.procedure,
          urgency:          form.urgency,
          anesthesia:       form.anesthesia,
          implants:         form.implants,
          preopClearance:   form.preop_clearance,
          facility:         form.facility,
          targetDate:       form.target_date,
          orderedBy:        order?.ordered_by || "",
          orderDate:        order?.order_date  || "",
          notes:            form.notes,
          coordinatorEmail: form.coordinator_email,
        }),
      });

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Server error ${res.status}`);
      }

      // Stamp booking_requested_at into the order's details JSONB
      const updatedDetails = {
        ...(order?.details || {}),
        procedure:            form.procedure,
        urgency:              form.urgency,
        anesthesia:           form.anesthesia,
        implants:             form.implants,
        preop_clearance:      form.preop_clearance,
        facility:             form.facility,
        target_date:          form.target_date,
        booking_requested_at: new Date().toISOString(),
        coordinator_email:    form.coordinator_email,
      };

      await db
        .from("patient_orders")
        .update({ details: updatedDetails, notes: form.notes })
        .eq("id", order.id);

      logAudit("patient_orders", "surgical_booking_requested", order.patient_id);

      setSent(true);
      onSent?.({ ...order, details: updatedDetails, notes: form.notes });
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
    // eslint-disable-next-line
  }, [form, order, patientName, patientMrn]);

  const content = (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        zIndex: 9000, display: "flex", alignItems: "center",
        justifyContent: "center", padding: 24,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "var(--navy-2)", border: "1px solid var(--border)",
        borderRadius: 12, width: "100%", maxWidth: 580,
        maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          padding: "18px 20px 14px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Surgical Booking Request</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
              {patientName} · MRN {patientMrn}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 4 }}
          >✕</button>
        </div>

        {sent ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12, color: "var(--teal)" }}>✓</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)", marginBottom: 8 }}>
              Booking request sent
            </div>
            <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 28, lineHeight: 1.6 }}>
              The surgery coordinator has been notified.<br />
              The order will remain in the queue until OR details are confirmed.
            </div>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <div style={{ padding: "18px 20px" }}>
            {/* Procedure */}
            <label style={{ display: "block", marginBottom: 14 }}>
              <div style={LABEL_ST}>Procedure *</div>
              <input
                value={form.procedure}
                onChange={e => set("procedure", e.target.value)}
                style={INPUT_ST}
                placeholder="e.g. Right carpal tunnel release"
              />
            </label>

            {/* Urgency + Anesthesia */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <label>
                <div style={LABEL_ST}>Urgency</div>
                <select value={form.urgency} onChange={e => set("urgency", e.target.value)} style={SELECT_ST}>
                  {URGENCY_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </label>
              <label>
                <div style={LABEL_ST}>Anesthesia type</div>
                <select value={form.anesthesia} onChange={e => set("anesthesia", e.target.value)} style={SELECT_ST}>
                  {ANESTHESIA_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </label>
            </div>

            {/* Target date + Facility */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <label>
                <div style={LABEL_ST}>Target date</div>
                <input
                  type="date"
                  value={form.target_date}
                  onChange={e => set("target_date", e.target.value)}
                  style={INPUT_ST}
                />
              </label>
              <label>
                <div style={LABEL_ST}>Preferred facility</div>
                <input
                  value={form.facility}
                  onChange={e => set("facility", e.target.value)}
                  style={INPUT_ST}
                  placeholder="e.g. Desert Surgery Center"
                />
              </label>
            </div>

            {/* Implants / equipment */}
            <label style={{ display: "block", marginBottom: 14 }}>
              <div style={LABEL_ST}>Implants / equipment needed</div>
              <textarea
                value={form.implants}
                onChange={e => set("implants", e.target.value)}
                style={{ ...INPUT_ST, height: 64, resize: "vertical", fontFamily: "inherit" }}
                placeholder="e.g. Standard carpal tunnel kit, tourniquet"
              />
            </label>

            {/* Pre-op clearance checkbox */}
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.preop_clearance}
                onChange={e => set("preop_clearance", e.target.checked)}
                style={{ width: 14, height: 14, accentColor: "var(--teal)", cursor: "pointer" }}
              />
              <span style={{ fontSize: 13, color: "var(--text-2)" }}>Pre-op medical clearance required</span>
            </label>

            {/* Notes to coordinator */}
            <label style={{ display: "block", marginBottom: 14 }}>
              <div style={LABEL_ST}>Notes to coordinator</div>
              <textarea
                value={form.notes}
                onChange={e => set("notes", e.target.value)}
                style={{ ...INPUT_ST, height: 72, resize: "vertical", fontFamily: "inherit" }}
                placeholder="Special instructions, patient requests, scheduling constraints…"
              />
            </label>

            {/* Coordinator email */}
            <label style={{ display: "block", marginBottom: 18 }}>
              <div style={LABEL_ST}>Coordinator email *</div>
              <input
                type="email"
                value={form.coordinator_email}
                onChange={e => set("coordinator_email", e.target.value)}
                style={INPUT_ST}
                placeholder="coordinator@surgerycenter.com"
              />
            </label>

            {/* Email preview */}
            <div style={{ marginBottom: 18 }}>
              <button
                type="button"
                onClick={() => setPreview(v => !v)}
                style={{ background: "none", border: "none", color: "var(--teal)", cursor: "pointer", fontSize: 12, padding: 0, textDecoration: "underline" }}
              >
                {preview ? "Hide" : "Preview"} email
              </button>
              {preview && (
                <pre style={{
                  marginTop: 8, padding: 12, borderRadius: 8, background: "var(--navy-3)",
                  fontSize: 11, color: "var(--text-2)", whiteSpace: "pre-wrap",
                  wordBreak: "break-word", lineHeight: 1.55, overflowX: "auto",
                }}>
                  {emailPreview}
                </pre>
              )}
            </div>

            {error && (
              <div style={{
                marginBottom: 12, padding: "8px 12px", borderRadius: 6,
                background: "rgba(224,82,82,0.12)", color: "#e05252", fontSize: 12,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
                {sending ? "Sending…" : "Send Booking Request"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
